import React, { useState, useEffect } from "react";
import * as adminService from "@/shared/api/services/adminService";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { createPageUrl, getProviderName } from "@/shared/utils";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Switch } from "@/shared/components/ui/switch";
import { Textarea } from "@/shared/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/shared/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import {
  ArrowLeft,
  Save,
  User,
  Key,
  Shield,
  Settings,
  Copy,
  Eye,
  EyeOff,
  CheckCircle2,
  Building2,
  Route,
  Lock,
  RefreshCw,
} from "lucide-react";
import FeeCommissionSection from "@/shared/components/forms/FeeCommissionSection";

const providers = [
  { code: "SCB", name: "Siam Commercial Bank" },
  { code: "KBANK", name: "Kasikorn Bank" },
  { code: "BBL", name: "Bangkok Bank" },
  { code: "KTB", name: "Krungthai Bank" },
  { code: "TRUEWALLET", name: "TrueMoney Wallet" },
  { code: "PROMPTPAY", name: "PromptPay QR" },
  { code: "LINEPAY", name: "LINE Pay" },
  { code: "SHOPEEPAY", name: "ShopeePay" },
];

// These will be translated in the component using t()
const getPermissionsList = (t) => [
  { key: "can_create_merchants", label: t("canCreateMerchants"), description: t("allowCreatingMerchantAccountsDirectly") },
  { key: "can_edit_fees", label: t("canEditFeeStructure"), description: t("allowModifyingFeeSettings") },
  { key: "can_view_webhook_logs", label: t("canViewWebhookLogs"), description: t("accessToRawWebhookData") },
  { key: "can_regenerate_api_keys", label: t("canRegenerateApiKeys"), description: t("allowRegeneratingApiCredentials") },
];

const getVisiblePages = (t) => [
  { key: "dashboard", label: t("dashboard") },
  { key: "merchants", label: t("merchants") },
  { key: "transactions", label: t("transactions") },
  { key: "withdrawals", label: t("withdrawals") },
  { key: "settings", label: t("settings") },
];

function generateApiKey(prefix = "rsl_pk_") {
  return prefix + Array.from({ length: 32 }, () =>
    "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789"[Math.floor(Math.random() * 62)]
  ).join("");
}

function generatePassword() {
  const length = 10;
  const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const lower = "abcdefghijklmnopqrstuvwxyz";
  const digits = "0123456789";
  const special = "!@#$%^&*()_+";
  const all = upper + lower + digits + special;

  let password = "";
  password += upper.charAt(Math.floor(Math.random() * upper.length));
  password += lower.charAt(Math.floor(Math.random() * lower.length));
  password += digits.charAt(Math.floor(Math.random() * digits.length));
  password += special.charAt(Math.floor(Math.random() * special.length));

  for (let i = password.length; i < length; i++) {
    password += all.charAt(Math.floor(Math.random() * all.length));
  }

  // Shuffle
  return password.split('').sort(() => 0.5 - Math.random()).join('');
}

export default function AdminResellerForm() {
  const { language, t } = useLanguage();
  const urlParams = new URLSearchParams(window.location.search);
  const resellerId = urlParams.get("id");
  const isEdit = !!resellerId;

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [currentTab, setCurrentTab] = useState("general");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    status: "ACTIVE",
    parent_type: "direct",
    engine_id: "",
    code: "",
    api_key: generateApiKey("rsl_pk_"),
    secret_key: generateApiKey("rsl_sk_"),
    deposit_fee: 0,
    withdrawal_fee: 0,
    percentage_fee: 1.5,
    commission_rate: 0.5,
    max_merchants: 10,
    allowed_providers: [],
    // Fee Commission Fields
    depositFeeAmount: 0,
    depositFeeType: "FIXED",
    withdrawalFeeAmount: 0,
    withdrawalFeeType: "FIXED",
    depositCommissionAmount: 0,
    depositCommissionType: "PERCENTAGE",
    withdrawalCommissionAmount: 0,
    withdrawalCommissionType: "PERCENTAGE",
    minDepositFee: "",
    maxDepositFee: "",
    minWithdrawalFee: "",
    maxWithdrawalFee: "",
    minDepositCommission: "",
    maxDepositCommission: "",
    minWithdrawalCommission: "",
    maxWithdrawalCommission: "",
    permissions: {
      can_create_merchants: true,
      can_edit_fees: false,
      can_view_webhook_logs: true,
      can_regenerate_api_keys: true,
      visible_pages: ["dashboard", "merchants", "transactions", "withdrawals", "settings"],
    },
    routing_override: {
      priority_providers: [],
      failover_enabled: true,
    },
    contact_name: "",
    contact_phone: "",
    support_email: "",
    notes: "",
  });

  const [showApiKey, setShowApiKey] = useState(false);
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState(null);
  const [saving, setSaving] = useState(false);

  const { data: existingReseller } = useQuery({
    queryKey: ["reseller", resellerId],
    queryFn: () => adminService.getResellerById(resellerId),
    enabled: isEdit,
  });


  const { data: engines = [] } = useQuery({
    queryKey: ["engines"],
    queryFn: () => adminService.getAllEngines(),
  });

  useEffect(() => {
    if (existingReseller) {
      setFormData(prev => ({
        ...prev,
        name: existingReseller.name,
        email: existingReseller.email,
        password: "",
        status: existingReseller.status,
        code: existingReseller.code,
        contact_name: existingReseller.contactName || "",
        contact_phone: existingReseller.contactPhone || "",
        support_email: existingReseller.supportEmail || "",
        notes: existingReseller.notes || "",
        deposit_fee: existingReseller.depositFee ?? 0,
        withdrawal_fee: existingReseller.withdrawalFee ?? 0,
        percentage_fee: existingReseller.percentageFee ?? 1.5,
        commission_rate: existingReseller.commissionRate ?? existingReseller.depositCommission ?? 0.5,
        max_merchants: existingReseller.maxMerchants ?? 10,
        allowed_providers: existingReseller.allowedProviders ?? [],
        permissions: existingReseller.permissions ?? prev.permissions,
        routing_override: existingReseller.routingOverride ?? prev.routing_override,
        api_key: existingReseller.apiKey ?? prev.api_key,
        secret_key: existingReseller.secretKey ?? prev.secret_key,
        parent_type: existingReseller.parentType?.toLowerCase() || "direct",
        // parent_id: existingReseller.parentId // If available in response
      }));
    }
  }, [existingReseller]);

  const createMutation = useMutation({
    mutationFn: (data) => adminService.createReseller(data),
    onSuccess: () => {
      queryClient.invalidateQueries(["admin-resellers"]);
      navigate(createPageUrl("AdminResellers"));
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data) => adminService.updateReseller(resellerId, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["admin-resellers"]);
      navigate(createPageUrl("AdminResellers"));
    },
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePermissionChange = (key, value) => {
    setFormData(prev => ({
      ...prev,
      permissions: { ...prev.permissions, [key]: value },
    }));
  };

  const handleVisiblePagesChange = (page) => {
    const currentPages = formData.permissions.visible_pages || [];
    const newPages = currentPages.includes(page)
      ? currentPages.filter(p => p !== page)
      : [...currentPages, page];
    handlePermissionChange("visible_pages", newPages);
  };

  const toggleProvider = (code) => {
    const current = formData.allowed_providers || [];
    const updated = current.includes(code)
      ? current.filter(p => p !== code)
      : [...current, code];
    handleChange("allowed_providers", updated);
  };

  const togglePriorityProvider = (code) => {
    const current = formData.routing_override.priority_providers || [];
    const updated = current.includes(code)
      ? current.filter(p => p !== code)
      : [...current, code];
    setFormData(prev => ({
      ...prev,
      routing_override: { ...prev.routing_override, priority_providers: updated },
    }));
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    setCopied(type);
    setTimeout(() => setCopied(null), 2000);
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      if (isEdit) {
        const updateData = {
          name: formData.name,
          code: formData.code,
          status: formData.status,
          contactName: formData.contact_name || undefined,
          contactPhone: formData.contact_phone || undefined,
          supportEmail: formData.support_email || undefined,
          notes: formData.notes || undefined,

          // Fees and Limits
          commissionRate: formData.commission_rate || undefined,
          depositFee: formData.deposit_fee || undefined,
          withdrawalFee: formData.withdrawal_fee || undefined,
          percentageFee: formData.percentage_fee || undefined,
          maxMerchants: formData.max_merchants,

          // Providers
          allowedProviders: formData.allowed_providers,
          routingOverride: formData.routing_override,

          // Permissions
          permissions: formData.permissions,
        };
        if (formData.password) {
          updateData.password = formData.password;
        }
        await updateMutation.mutateAsync(updateData);
      } else {
        // For create, send required fields
        if (!formData.email || !formData.password || !formData.name) {
          toast.error(t("pleaseFillAllRequiredFields"));
          setSaving(false);
          return;
        }

        const createData = {
          // General
          name: formData.name,
          code: formData.code,
          email: formData.email,
          password: formData.password,
          parentType: formData.parent_type,
          parentId: formData.engine_id || formData.parent_id,
          status: formData.status,
          contactName: formData.contact_name || undefined,
          contactPhone: formData.contact_phone || undefined,
          supportEmail: formData.support_email || undefined,
          notes: formData.notes || undefined,
          // Fees and Limits
          commissionRate: formData.commission_rate || undefined,
          depositFee: formData.deposit_fee || undefined,
          withdrawalFee: formData.withdrawal_fee || undefined,
          percentageFee: formData.percentage_fee || undefined,
          maxMerchants: formData.max_merchants,
          // Providers
          allowedProviders: formData.allowed_providers,
          routingOverride: formData.routing_override,
          // Permissions
          permissions: formData.permissions,
          // API
          apiKey: formData.api_key,
          secretKey: formData.secret_key,
        };

        // Add parent reference if engine is selected
        if (formData.parent_type === "engine" && formData.engine_id) {
          createData.engineId = formData.engine_id;
        }

        await createMutation.mutateAsync(createData);
      }
    } catch (error) {
      // Error is handled by global error handler
      setSaving(false);
    }
  };

  const handleNext = (nextTab) => {
    setCurrentTab(nextTab);
  };

  const tabOrder = ["general", "fees", "providers", "permissions", "api"];
  const currentTabIndex = tabOrder.indexOf(currentTab);
  const isLastTab = currentTabIndex === tabOrder.length - 1;
  const isFirstTab = currentTabIndex === 0;

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to={createPageUrl("AdminResellers")}>
          <Button variant="ghost" size="icon">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            {isEdit ? t("editReseller") : t("createNewReseller")}
          </h1>
          <p className="text-slate-500">
            {isEdit ? t("updateResellerDetails") : t("setUpNewResellerPartner")}
          </p>
        </div>
      </div>

      <Tabs value={currentTab} onValueChange={setCurrentTab} className="space-y-6">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="general" className="gap-2">
            <User className="h-4 w-4" />
            {t("generalTab")}
          </TabsTrigger>
          <TabsTrigger value="fees" className="gap-2">
            <Building2 className="h-4 w-4" />
            {t("feesLimitsTab")}
          </TabsTrigger>
          <TabsTrigger value="providers" className="gap-2">
            <Route className="h-4 w-4" />
            {t("providers")}
          </TabsTrigger>
          <TabsTrigger value="permissions" className="gap-2">
            <Lock className="h-4 w-4" />
            {t("permissionsTab")}
          </TabsTrigger>
          <TabsTrigger value="api" className="gap-2">
            <Key className="h-4 w-4" />
            {t("apiKeys")}
          </TabsTrigger>
        </TabsList>

        {/* General Tab */}
        <TabsContent value="general">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card>
              <CardHeader>
                <CardTitle>{t("generalInformation")}</CardTitle>
                <CardDescription>{t("resellerProfileAndContact")}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>{t("businessNameRequired")}</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    placeholder={t("businessNamePlaceholder")}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t("resellerCode")}</Label>
                  <Input
                    value={formData.code}
                    onChange={(e) => handleChange("code", e.target.value.toUpperCase())}
                    placeholder="RSL001"
                  />
                </div>

                {!isEdit && (
                  <div className="space-y-2">
                    <Label>{t("parentType")}</Label>
                    <Select
                      value={formData.parent_type || "direct"}
                      onValueChange={(v) => {
                        handleChange("parent_type", v);
                        handleChange("engine_id", "");
                      }}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="direct">{t("directAdmin")}</SelectItem>
                        <SelectItem value="engine">{t("engines")}</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500">{t("whoManagesThisReseller")}</p>
                  </div>
                )}

                {!isEdit && formData.parent_type === "engine" && (
                  <div className="space-y-2">
                    <Label>{t("engine")}</Label>
                    <Select
                      value={formData.engine_id || ""}
                      onValueChange={(v) => handleChange("engine_id", v)}
                    >
                      <SelectTrigger><SelectValue placeholder={t("selectEngine")} /></SelectTrigger>
                      <SelectContent>
                        {engines.map((e) => (
                          <SelectItem key={e.id} value={e.id}>
                            {e.name} {e.email && `(${e.email})`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>{t("loginEmailRequired")}</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    placeholder="admin@reseller.com"
                    disabled={isEdit}
                    required
                    autoComplete="off"
                  />
                  {isEdit && <p className="text-xs text-slate-500">{t("emailCannotBeChangedAfterCreation")}</p>}
                </div>

                <div className="space-y-2">
                  <Label>{isEdit ? t("newPasswordLeaveBlank") : t("passwordRequired")}</Label>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <div className="relative flex-1">
                      <Input
                        type={showPassword ? "text" : "password"}
                        value={formData.password}
                        onChange={(e) => handleChange("password", e.target.value)}
                        placeholder={isEdit ? t("enterNewPassword") : t("enterPassword")}
                        required={!isEdit}
                        className="pr-10"
                        autoComplete="new-password"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-0 top-0 h-full px-3 text-slate-400 hover:text-slate-600 hover:bg-transparent"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-10 px-3 flex items-center gap-2 group border border-blue-100 hover:border-blue-600 hover:bg-blue-600 hover:text-white text-blue-600 font-medium transition-all duration-300 rounded-lg shadow-sm w-full sm:w-auto flex-shrink-0"
                      onClick={() => {
                        const newPass = generatePassword();
                        handleChange("password", newPass);
                        navigator.clipboard.writeText(newPass);
                      }}
                    >
                      <RefreshCw className="h-4 w-4 group-hover:rotate-180 transition-transform duration-500" />
                      <span className="text-xs uppercase tracking-wide">{t("generatePassword")}</span>
                    </Button>
                  </div>
                </div>

                {isEdit && (
                  <div className="space-y-2">
                    <Label>{t("status")}</Label>
                    <Select value={formData.status} onValueChange={(v) => handleChange("status", v)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ACTIVE">{t("active")}</SelectItem>
                        <SelectItem value="INACTIVE">{t("inactive")}</SelectItem>
                        <SelectItem value="SUSPENDED">{t("suspended")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t("contactName")}</Label>
                    <Input
                      value={formData.contact_name}
                      onChange={(e) => handleChange("contact_name", e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t("contactPhone")}</Label>
                    <Input
                      value={formData.contact_phone}
                      onChange={(e) => handleChange("contact_phone", e.target.value)}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t("supportEmail")}</Label>
                  <Input
                    type="email"
                    value={formData.support_email}
                    onChange={(e) => handleChange("support_email", e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t("notes")}</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => handleChange("notes", e.target.value)}
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>

        {/* Fees & Limits Tab */}
        <TabsContent value="fees">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <FeeCommissionSection
              feeData={{
                depositFeeAmount: formData.depositFeeAmount,
                depositFeeType: formData.depositFeeType,
                withdrawalFeeAmount: formData.withdrawalFeeAmount,
                withdrawalFeeType: formData.withdrawalFeeType,
                depositCommissionAmount: formData.depositCommissionAmount,
                depositCommissionType: formData.depositCommissionType,
                withdrawalCommissionAmount: formData.withdrawalCommissionAmount,
                withdrawalCommissionType: formData.withdrawalCommissionType,
                minDepositFee: formData.minDepositFee,
                maxDepositFee: formData.maxDepositFee,
                minWithdrawalFee: formData.minWithdrawalFee,
                maxWithdrawalFee: formData.maxWithdrawalFee,
                minDepositCommission: formData.minDepositCommission,
                maxDepositCommission: formData.maxDepositCommission,
                minWithdrawalCommission: formData.minWithdrawalCommission,
                maxWithdrawalCommission: formData.maxWithdrawalCommission,
              }}
              onChange={(newFeeData) => {
                setFormData((prev) => ({ ...prev, ...newFeeData }));
              }}
              entityType="RESELLER"
              entityId={resellerId}
              showConstraints={true}
              description="Configure deposit and withdrawal fees for this reseller"
            />

            <Card className="mt-6">
              <CardHeader>
                <CardTitle>{t("operationalLimits")}</CardTitle>
                <CardDescription>{t("manageResellerCapabilitiesAndLimits")}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label>{t("maxMerchantsLabel")}</Label>
                  <Input
                    type="number"
                    value={formData.max_merchants}
                    onChange={(e) => handleChange("max_merchants", parseInt(e.target.value))}
                  />
                  <p className="text-xs text-slate-500">{t("maximumMerchantsResellerCanCreate")}</p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>

        {/* Providers Tab */}
        <TabsContent value="providers">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>{t("allowedProviders")}</CardTitle>
                <CardDescription>{t("selectWhichPaymentProviders")}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {providers.map(provider => (
                    <label
                      key={provider.code}
                      className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-colors ${formData.allowed_providers?.includes(provider.code)
                        ? "bg-blue-50 border-blue-300"
                        : "bg-white border-slate-200 hover:border-slate-300"
                        }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.allowed_providers?.includes(provider.code)}
                        onChange={() => toggleProvider(provider.code)}
                        className="rounded border-slate-300"
                      />
                      <span className="text-sm font-medium">{getProviderName(provider, language)}</span>
                    </label>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{t("routingOverride")}</CardTitle>
                <CardDescription>{t("configureRoutingBehavior")}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                  <div>
                    <p className="font-medium">{t("failoverEnabled")}</p>
                    <p className="text-sm text-slate-500">{t("automaticallyTryNextProvider")}</p>
                  </div>
                  <Switch
                    checked={formData.routing_override?.failover_enabled}
                    onCheckedChange={(v) => setFormData(prev => ({
                      ...prev,
                      routing_override: { ...prev.routing_override, failover_enabled: v },
                    }))}
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t("priorityProvidersInOrder")}</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {providers.filter(p => formData.allowed_providers?.includes(p.code)).map(provider => (
                      <label
                        key={provider.code}
                        className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors text-sm ${formData.routing_override?.priority_providers?.includes(provider.code)
                          ? "bg-emerald-50 border-emerald-300"
                          : "bg-white border-slate-200"
                          }`}
                      >
                        <input
                          type="checkbox"
                          checked={formData.routing_override?.priority_providers?.includes(provider.code)}
                          onChange={() => togglePriorityProvider(provider.code)}
                          className="rounded border-slate-300"
                        />
                        <span>{provider.code}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>

        {/* Permissions Tab */}
        <TabsContent value="permissions">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>{t("featurePermissions")}</CardTitle>
                <CardDescription>{t("controlWhatActionsReseller")}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {getPermissionsList(t).map(perm => (
                  <div key={perm.key} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                    <div>
                      <p className="font-medium">{perm.label}</p>
                      <p className="text-sm text-slate-500">{perm.description}</p>
                    </div>
                    <Switch
                      checked={formData.permissions?.[perm.key]}
                      onCheckedChange={(v) => handlePermissionChange(perm.key, v)}
                    />
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{t("visibleDashboardPages")}</CardTitle>
                <CardDescription>{t("controlWhichPagesReseller")}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                  {getVisiblePages(t).map(page => (
                    <label
                      key={page.key}
                      className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-colors ${formData.permissions?.visible_pages?.includes(page.key)
                        ? "bg-blue-50 border-blue-300"
                        : "bg-white border-slate-200"
                        }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.permissions?.visible_pages?.includes(page.key)}
                        onChange={() => handleVisiblePagesChange(page.key)}
                        className="rounded border-slate-300"
                      />
                      <span className="text-sm font-medium">{page.label}</span>
                    </label>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>

        {/* API Keys Tab */}
        <TabsContent value="api">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card>
              <CardHeader>
                <CardTitle>{t("apiCredentials")}</CardTitle>
                <CardDescription>{t("resellerApiKeysForIntegration")}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                  <div className="flex gap-3">
                    <Shield className="h-5 w-5 text-amber-600 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-amber-900">{t("securityNotice")}</p>
                      <p className="text-sm text-amber-700">
                        {t("apiKeysAutoGenerated")}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t("apiKeyPublic")}</Label>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 p-3 bg-slate-900 rounded-lg font-mono text-sm text-white overflow-x-auto">
                      {showApiKey ? formData.api_key : "••••••••••••••••••••••••••••••••"}
                    </div>
                    <Button variant="outline" size="icon" onClick={() => setShowApiKey(!showApiKey)}>
                      {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                    <Button variant="outline" size="icon" onClick={() => copyToClipboard(formData.api_key, "api")}>
                      {copied === "api" ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t("secretKey")}</Label>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 p-3 bg-slate-900 rounded-lg font-mono text-sm text-white overflow-x-auto">
                      {showSecretKey ? formData.secret_key : "••••••••••••••••••••••••••••••••"}
                    </div>
                    <Button variant="outline" size="icon" onClick={() => setShowSecretKey(!showSecretKey)}>
                      {showSecretKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                    <Button variant="outline" size="icon" onClick={() => copyToClipboard(formData.secret_key, "secret")}>
                      {copied === "secret" ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>
      </Tabs>

      {/* Footer Actions */}
      <div className="flex items-center justify-between pt-4 border-t">
        <div className="flex gap-2">
          <Link to={createPageUrl("AdminResellers")}>
            <Button variant="outline">{t("cancel")}</Button>
          </Link>
          {!isFirstTab && (
            <Button variant="outline" onClick={() => handleNext(tabOrder[currentTabIndex - 1])}>
              {t("backButton")}
            </Button>
          )}
        </div>

        {!isLastTab ? (
          <Button onClick={() => handleNext(tabOrder[currentTabIndex + 1])}>
            {t("nextButton")}
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            disabled={saving || !formData.name || !formData.email || (!isEdit && !formData.password)}
            className="gap-2"
          >
            <Save className="h-4 w-4" />
            {saving ? t("saving") : isEdit ? t("updateReseller") : t("createResellerButton")}
          </Button>
        )}
      </div>
    </div>
  );
}